import type { ReportingDataInput } from '@risksmart-app/shared/reporting/api/schema';
import type { ReportingDataResponse } from '@risksmart-app/trpc/src/types';
import type { GetReportingDataQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetReportingDataDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createQueryHook } from 'src/utils';

import { mapReportingDataToGraphQL } from './mapReportingData';

// A skipped query still evaluates its args to build a query key and GraphQL
// variables, so callers that haven't resolved a datasource yet pass a stable
// empty input. The query is disabled via `shouldSkip`, so this is never sent.
const EMPTY_REPORTING_DATA_INPUT: ReportingDataInput = {
  dataSources: [],
  fields: [],
  filters: { operation: 'and', filters: [] },
  limit: 0,
  offset: 0,
};

const useGetReportingDataBase = createQueryHook<
  ReportingDataInput,
  ReportingDataResponse,
  GetReportingDataQuery
>({
  trpcQueryOptions: (trpc, args) =>
    trpc.frontend.reporting.reportingData.queryOptions(args),
  mapTrpcDataToGraphQL: mapReportingDataToGraphQL,
  graphqlDocument: GetReportingDataDocument,
  graphqlVariables: (args) => ({ Input: args }),
  // Matches the pre-migration consumers (both the widget and the detail page
  // read with `no-cache`). `reportingData` is an unkeyed array-of-arrays field
  // on the query root, so caching it would trigger Apollo's "Cache data may be
  // lost when replacing the reportingData field" merge warning — which vitest
  // promotes to a fatal. No consumer mounts this read concurrently, so there is
  // nothing to dedup.
  graphqlFetchPolicy: 'no-cache',
  // Every consumer of this read already renders its own translated failure UI
  // (the detail page and the widget both show an inline `Alert`), so the
  // factory's raw-message toast would be a second notification for one failure.
  suppressErrorNotification: true,
});

/**
 * `getReportingData` read (RSP-6504). Wraps the base hook so callers can pass an
 * undefined input before a datasource resolves — the query is skipped and a
 * stable empty input keeps the query key stable.
 */
export const useGetReportingData = ({
  queryArgs,
  shouldSkip,
}: {
  queryArgs: ReportingDataInput | undefined;
  shouldSkip?: boolean;
}) =>
  useGetReportingDataBase({
    queryArgs: queryArgs ?? EMPTY_REPORTING_DATA_INPUT,
    shouldSkip: shouldSkip || !queryArgs,
  });
